import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import HistoryIcon from 'assets/LeadInfo/history.svg';
import ResizeIcon from 'assets/LeadInfo/resize.svg';
import SalesIcon from 'assets/LeadInfo/sales.svg';
import UserIcon from 'assets/LeadInfo/user.svg';
import { getLeadHistory, getWorkflowStatuses } from 'features/newsales/newsalesActions';

import {
  ActivityAuthor,
  ActivityDate,
  ActivityHistory,
  ActivityInfo,
  ActivityStatus,
  HistoryContent,
  HistoryTitle,
  ResizeImg,
  SalesBlockIcon,
  SalesContent,
  SalesImgBox,
  SalesScript,
  SubTitle,
  TimelineItem,
  TimelineLine,
  Title,
  TitleBlock,
  TitleIcon,
  UserAvatar,
} from './HistoryInfo.styles';

const HistoryInfo = ({ leadData }) => {
  const dispatch = useDispatch();
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const [workflowStatusMap, setWorkflowStatusMap] = useState({});

  const fetchWorkflowStatuses = useCallback(async () => {
    try {
      const response = await dispatch(getWorkflowStatuses()).unwrap();
      const statuses = Array.isArray(response) ? response : response?.data || [];
      const statusMap = {};
      statuses.forEach((status) => {
        statusMap[status.id || status.uuid] = status.name;
      });
      setWorkflowStatusMap(statusMap);
    } catch (error) {
      console.error('Failed to fetch workflow statuses:', error);
    }
  }, [dispatch]);

  const fetchHistory = useCallback(async () => {
    if (!leadData?.id) return;

    setIsLoadingHistory(true);
    setHistoryError(null);
    try {
      const response = await dispatch(getLeadHistory({ leadId: leadData.id })).unwrap();
      const historyArray = Array.isArray(response)
        ? response
        : response?.data
          ? Array.isArray(response.data)
            ? response.data
            : []
          : [];
      setHistory(historyArray);
    } catch (error) {
      setHistoryError(error);
      setHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [leadData?.id, dispatch]);

  useEffect(() => {
    fetchWorkflowStatuses();
  }, [fetchWorkflowStatuses]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const getActionText = (action) => {
    switch (action) {
      case 'created':
        return 'Lead Created';
      case 'status_changed':
        return 'Status Changed';
      case 'assigned':
        return 'Lead Assigned';
      case 'reassigned':
        return 'Lead Reassigned';
      default:
        return action;
    }
  };

  const getUserDisplayName = (changedBy) => {
    if (!changedBy) return 'Unknown';

    const name = changedBy.name || changedBy.email || 'Unknown';
    const roleOrOccupation = changedBy.occupation || changedBy.userType;

    if (roleOrOccupation) {
      return `${name} / ${roleOrOccupation}`;
    }
    return name;
  };

  const getStatusName = (statusId) => {
    return workflowStatusMap[statusId] || statusId;
  };

  return (
    <>
      <SalesScript>
        <SalesContent>
          <SalesImgBox>
            <SalesBlockIcon src={SalesIcon} />
          </SalesImgBox>
          <TitleBlock>
            <Title>Sales Script</Title>
            <SubTitle>View talking points and objection handlers</SubTitle>
          </TitleBlock>
        </SalesContent>
        <ResizeImg src={ResizeIcon} />
      </SalesScript>

      {!isLoadingHistory && !historyError && history.length > 0 && (
        <ActivityHistory>
          <HistoryTitle>
            <TitleIcon src={HistoryIcon} />
            Activity History
          </HistoryTitle>
          <HistoryContent>
            {history.map((item) => (
              <TimelineItem key={item.id}>
                <TimelineLine />
                <UserAvatar>
                  <img src={UserIcon} alt="user" />
                </UserAvatar>
                <ActivityInfo>
                  {item.changedBy && (
                    <ActivityAuthor>
                      {item.action === 'created' ? 'Created by' : 'Changed by'}{' '}
                      <span>{getUserDisplayName(item.changedBy)}</span>
                    </ActivityAuthor>
                  )}
                  {item.action === 'status_changed' &&
                    item.fieldName === 'workflowStatusId' &&
                    (item.oldValue || item.newValue) && (
                      <ActivityStatus>
                        {item.oldValue && (
                          <>
                            From{' '}
                            <span className="old">{getStatusName(item.oldValueName)}</span>
                          </>
                        )}
                        {item.oldValue && item.newValue && ' to '}
                        {item.newValue && (
                          <span className="new">{getStatusName(item.newValueName)}</span>
                        )}
                      </ActivityStatus>
                    )}
                  {item.action === 'created' && (
                    <ActivityStatus>{getActionText(item.action)}</ActivityStatus>
                  )}
                  {(item.changedAt || item.createdAt) && (
                    <ActivityDate>
                      {new Date(item.changedAt || item.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </ActivityDate>
                  )}
                </ActivityInfo>
              </TimelineItem>
            ))}
          </HistoryContent>
        </ActivityHistory>
      )}
    </>
  );
};

export default HistoryInfo;
